import { useState } from 'react';
import {
  AddressSearchType, useAddressFindByIdApi, useAddressFindByPostCodeApi, useAddressSearchApi,
} from '../../api';
import { useDelayCall } from '../../util';
import {
  ModelInput, ModelSelectSearchInput, SearchListBox, useModelForm,
} from '../form';
import { EmptyInput, InputGroup, InputRow } from '../InputGroup';
import { Errors } from '../../types';
import {
  SearchIcon,
  InfoContainer,
  WarningInfoDanger,
  useFormConfigurationContext,
} from '../../..';

export function AddressSearch({
  field,
  addressId,
  countries = {},
  disabled = false,
  limitToGreatBritain = false,
  searchPlaceholder = 'Enter postcode or first line of address',
  subBuildingLabel = 'Flat / sub-building number',
  buildingNameLabel = 'House / building name',
  buildingNumberLabel = 'House / building number',
  label = 'Find address',
  errors,
  onBlur = () => { },
  onSelect = () => { },
}: {
  field: string,
  addressId: string,
  countries?: Record<string, string>,
  disabled?: boolean,
  limitToGreatBritain?: boolean,
  searchPlaceholder?: string,
  subBuildingLabel?: string,
  buildingNameLabel?: string,
  buildingNumberLabel?: string,
  label?: string,
  errors: Errors,
  onBlur?: () => void,
  onSelect?: () => void,
}) {
  const [search, setSearch] = useState<string>('');
  const [searchAddresses, setSearchAddresses] = useState<AddressSearchType[]>([]);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [addressSelected, setAddressSelected] = useState<boolean>(false);

  const addressSearchApi = useAddressSearchApi();
  const addressFindByIdApi = useAddressFindByIdApi();
  const addressFindByPostCodeApi = useAddressFindByPostCodeApi();

  useDelayCall(() => {
    (async () => {
      const data = await addressSearchApi.get(search, limitToGreatBritain);
      setSearchAddresses(data);
      setShowWarning(!!search && !data?.length);
    })();
  }, [search]);

  const { update, value } = useModelForm(`${field}`);
  const {
    postCode, houseName, subBuilding, houseNumber, country,
  } = value;

  const [postCodeCountryMatches, setPostCodeCountryMatches] = useState<string[]>([]);

  const { isDisabled } = useFormConfigurationContext();

  useDelayCall(() => {
    (async () => {
      if (isDisabled || disabled) return;
      if (postCode) {
        const { matchedCountries } = await addressFindByPostCodeApi.get(postCode);
        setPostCodeCountryMatches(matchedCountries?.map((matchedCountry) => matchedCountry.country));
        if (matchedCountries?.length === 1) {
          if (matchedCountries[0]?.countryIsoCode === country) return;
          update({ [`${field}.country`]: matchedCountries[0]?.countryIsoCode });
        } else if (matchedCountries?.length) {
          const countryCodes = matchedCountries?.map((matchedCountry) => matchedCountry.countryIsoCode);
          if (country && !countryCodes.includes(country)) {
            update({ [`${field}.country`]: null });
          }
        }
      } else {
        setPostCodeCountryMatches([]);
      }
    })();
  }, [postCode, isDisabled, disabled], true);

  const onChangeAddressId = async (newAddressId?: string) => {
    if (newAddressId) {
      const data = await addressFindByIdApi.get(newAddressId);

      update({
        [`${field}`]: {
          ...value,
          subBuilding: data?.subBuilding,
          postTown: data?.postTown,
          houseNumber: data?.houseNumber,
          postCode: data?.postCode,
          houseName: data?.houseName,
          country: data?.countryIsoCode,
          street: data?.street,
        },
      });
      setAddressSelected(true);
      onSelect();
    }
  };

  const nonPropertyErrors = Object.values(errors?.[addressId] ?? {})?.filter((et) => !(et?.propertyName));

  const [inputTouched, setInputTouched] = useState<boolean>(false);
  const handleInputTouched = () => {
    onBlur();
    setInputTouched(true);
  };

  return (
    <InputGroup>
      {!disabled && (
        <InputRow>
          <SearchListBox
            id={`${addressId}-address-search`}
            searchValue={search}
            searchPlaceholder={searchPlaceholder}
            label={label}
            options={
              showWarning
                ? { warning: 'Address not found, please retry or manually enter the address' }
                : searchAddresses.reduce((prev, current) => ({
                  ...prev,
                  [current.id]: current.text,
                }), {})
            }
            onChangeSearch={setSearch}
            onChangeItem={onChangeAddressId}
            required
            prefix={<SearchIcon />}
            allowItemSelect={!showWarning}
          />
        </InputRow>
      )}
      <InputRow>
        <ModelInput
          label={subBuildingLabel}
          field={`${field}.subBuilding`}
          disabled={disabled}
          onBlur={handleInputTouched}
          touchOverride={addressSelected}
        />
        <ModelInput
          label={buildingNumberLabel}
          field={`${field}.houseNumber`}
          required={!houseName}
          disabled={disabled}
          onBlur={handleInputTouched}
          touchOverride={addressSelected}
        />
      </InputRow>
      <InputRow>
        <ModelInput
          label={buildingNameLabel}
          field={`${field}.houseName`}
          required={!houseNumber}
          disabled={disabled}
          onBlur={handleInputTouched}
          touchOverride={addressSelected}
        />
        <ModelInput
          label="Street"
          field={`${field}.street`}
          required={!houseName || !!subBuilding || !!houseNumber}
          disabled={disabled}
          onBlur={handleInputTouched}
          touchOverride={addressSelected}
        />
      </InputRow>
      <InputRow>
        <ModelInput
          label="Town / city"
          field={`${field}.postTown`}
          disabled={disabled}
          required
          onBlur={handleInputTouched}
          touchOverride={addressSelected}
        />
        <ModelInput
          label="Postcode"
          field={`${field}.postCode`}
          required
          disabled={disabled}
          onBlur={handleInputTouched}
          touchOverride={addressSelected}
        />
      </InputRow>
      <InputRow>
        <ModelSelectSearchInput
          label="Country"
          field={`${field}.country`}
          options={
            Object.entries(countries)
              .filter(([, ctry]) => !postCodeCountryMatches?.length || postCodeCountryMatches.includes(ctry))
              .reduce((prev, [key, current]) => ({
                ...prev,
                [key]: current,
              }), {})
          }
          required
          disabled={disabled || postCodeCountryMatches?.length === 1}
          placeholder={(disabled || postCodeCountryMatches?.length === 1) ? '' : 'Please select'}
          onBlur={handleInputTouched}
        />
        <EmptyInput />
      </InputRow>

      {((addressSelected || inputTouched) && nonPropertyErrors?.length > 0) && (
        <InfoContainer>
          {nonPropertyErrors?.map((et) => (
            et?.errors?.map((error) => (
              <WarningInfoDanger key={error?.errorMessage}>
                {error.errorMessage}
              </WarningInfoDanger>
            ))
          ))}
        </InfoContainer>
      )}
    </InputGroup>
  );
}
